// DocAutomationPage — 물류 문서 자동화(② AI 문서 자동화).
// 거래명세서·송장·정산서 업로드 → Claude 비전 OCR → 구조화 추출 → 검수·수정 → 엑셀 내보내기.
// PII는 서버에서 자동 마스킹 후 저장됨(담당자명·연락처 등).
import { useState, useEffect } from 'react';
import { IconFileInvoice } from '@tabler/icons-react';
import AppShell from '../components/AppShell';
import useIsMobile from '../hooks/useIsMobile';
import { docExtractAPI } from '../api/client';
import { Page, PageHeader, Button } from '../components/ui';
import { COLORS, RADIUS, card as cardBase, input as inputBase } from '../utils/uiStyles';   // 색 = uiStyles 토큰·--gw-* 변수(시안 F)
import { fmtKstShort } from '../utils/kstTime';   // 서버 UTC naive → KST 표시(2026-10-03)
import { alertDialog, confirmDialog } from '../components/AppDialog';

const CONF = { high: { t: '높음', c: COLORS.successText }, medium: { t: '보통', c: COLORS.warn }, low: { t: '낮음', c: COLORS.dangerText } };
const STATUS = { pending: { t: '검수 대기', c: COLORS.warn }, reviewed: { t: '검수 완료', c: COLORS.successText }, rejected: { t: '반려', c: COLORS.dangerText } };

export default function DocAutomationPage() {
  const mobile = useIsMobile();   // 모바일: 목록↔상세 단일 컬럼(상세 minWidth 420 제거)
  const [types, setTypes] = useState([]);
  const [docType, setDocType] = useState('statement');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [sel, setSel] = useState(null);     // 상세(편집 중) 레코드
  const [dirty, setDirty] = useState(false);

  const loadList = async () => {
    setListLoading(true);
    try { const r = await docExtractAPI.list(); setList(r.data || []); }
    catch { setList([]); }
    finally { setListLoading(false); }
  };
  useEffect(() => {
    docExtractAPI.types().then((r) => setTypes(r.data || [])).catch(() => {});
    loadList();
  }, []);

  const typeMeta = (k) => types.find((t) => t.key === k) || {};
  const upload = async () => {
    if (!file) { setErr('파일을 선택하세요.'); return; }
    setErr(''); setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const r = await docExtractAPI.create(fd);
      setFile(null);
      await loadList();
      openDetail(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || '문서 추출에 실패했습니다. 이미지/PDF 화질을 확인하세요.');
    } finally { setBusy(false); }
  };

  const openDetail = (rec) => { setSel(JSON.parse(JSON.stringify(rec))); setDirty(false); };
  const setField = (k, v) => { setSel((s) => ({ ...s, fields: { ...s.fields, [k]: v } })); setDirty(true); };
  const setCell = (i, c, v) => { setSel((s) => { const li = s.line_items.slice(); li[i] = { ...li[i], [c]: v }; return { ...s, line_items: li }; }); setDirty(true); };

  const save = async (status) => {
    if (!sel) return;
    try {
      const body = { fields: sel.fields, line_items: sel.line_items };
      if (status) body.review_status = status;
      const r = await docExtractAPI.update(sel.id, body);
      setSel(r.data); setDirty(false); loadList();
    } catch (e) { alertDialog('저장 실패: ' + (e.response?.data?.detail || e.message)); }
  };
  const remove = async (rec) => {
    if (!(await confirmDialog('이 추출 결과를 삭제할까요?', { danger: true }))) return;
    try { await docExtractAPI.remove(rec.id); if (sel?.id === rec.id) setSel(null); loadList(); } catch (e) { alertDialog('삭제 실패'); }
  };
  const downloadExcel = async (rec) => {
    try {
      const r = await docExtractAPI.excel(rec.id);
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${rec.doc_label || 'extract'}_${rec.id}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { alertDialog('엑셀 내보내기 실패'); }
  };

  // 입력 = uiStyles input 그대로(outline 은 따로 주지 않는다 — focus-visible 링을 가리지 않게)
  const inp = { ...inputBase };
  // 품목 표 머리 = DataTable th 와 같은 값(칸마다 입력이 있어 DataTable 로 바꾸지 않는다 · 열이 서식마다 달라 머리 아이콘 없음)
  const th = { textAlign: 'left', height: '33px', padding: '5px 9px', fontSize: '12px', fontWeight: 400, color: 'var(--gw-muted, #92918d)', background: 'transparent', borderBottom: '1px solid var(--gw-line, #eeeeec)', whiteSpace: 'nowrap' };
  const card = { ...cardBase, padding: 0 };   // 쓰는 곳마다 padding 을 따로 준다

  return (
    <AppShell app="docauto" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        <Page>
          <PageHeader
            icon={IconFileInvoice}
            tone="blue"
            title="AI 문서 자동화"
            subtitle={<>
              거래명세서·송장·정산서를 업로드하면 AI가 표 형태로 자동 추출합니다. 추출 결과는 <b>검수·수정</b> 후 엑셀로 내보낼 수 있습니다.
              개인정보(담당자명·연락처 등)는 <b>자동 마스킹</b>되어 저장됩니다. <span style={{ color: 'var(--gw-yellow-ink, #8d6d28)' }}>추출은 보조 도구이니 금액·수량은 반드시 원본과 대조하세요.</span>
            </>}
          />

          {/* 업로드 카드 */}
          <div style={{ ...card, padding: '14px', marginBottom: '18px', display: 'grid', gridTemplateColumns: mobile ? '1fr' : '180px minmax(220px, 1fr) auto', gap: '10px', alignItems: 'center' }}>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} style={inp}>
              {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files[0] || null)} style={{ fontSize: '13px', minWidth: 0 }} />
            <Button variant="primary" onClick={upload} disabled={busy}>{busy ? 'AI 추출 중…' : '업로드 · 추출'}</Button>
            {err && <div style={{ gridColumn: '1 / -1', fontSize: '12px', color: COLORS.dangerText, background: COLORS.dangerSoft, border: 'none', borderRadius: RADIUS.md, padding: '8px 10px' }}>{err}</div>}
          </div>

          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* 좌: 목록 */}
            <div style={{ flex: mobile ? '1 1 100%' : '0 0 300px', maxWidth: '100%' }}>
              <div style={{ fontSize: '13px', fontWeight: 620, marginBottom: '8px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span>추출 이력</span><span style={{ color: 'var(--gw-muted, #92918d)', fontWeight: 500 }}>{list.length}건</span>
              </div>
              {/* 높이 상한 없음 — 목록은 페이지 스크롤로 내려 본다(머리가 커져 안쪽 스크롤과 겹치지 않게) */}
              <div style={{ ...card, padding: '6px' }}>
                {listLoading && <div style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '28px 10px', textAlign: 'center' }}>불러오는 중…</div>}
                {!listLoading && list.length === 0 && <div style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '28px 10px', textAlign: 'center' }}>아직 추출한 문서가 없습니다.</div>}
                {!listLoading && list.map((r) => {
                  const st = STATUS[r.review_status] || STATUS.pending;
                  return (
                    <div key={r.id} onClick={() => openDetail(r)} style={{ padding: '10px 12px', borderRadius: RADIUS.md, marginBottom: '4px', cursor: 'pointer', background: sel?.id === r.id ? 'var(--gw-selected, #eaeae7)' : 'var(--gw-surface, #fff)', border: '1px solid', borderColor: sel?.id === r.id ? 'var(--gw-selected, #eaeae7)' : 'var(--gw-line, #eeeeec)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.source_filename || '(파일명 없음)'}</div>
                      <div style={{ fontSize: '11px', color: 'var(--gw-secondary, #64635f)', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <span>{r.doc_label}</span>
                        <span style={{ color: st.c, fontWeight: 600 }}>{st.t}</span>
                        <span>{fmtKstShort(r.created_at)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 우: 상세/검수 */}
            <div style={{ flex: 1, minWidth: mobile ? 0 : '420px', width: mobile ? '100%' : undefined }}>
              {!sel && <div style={{ ...card, fontSize: '13px', color: 'var(--gw-muted, #92918d)', padding: '34px 20px', textAlign: 'center' }}>왼쪽에서 문서를 선택하거나, 위에서 새 문서를 업로드하세요.</div>}
              {sel && (() => {
                const meta = typeMeta(sel.doc_type);
                const cols = meta.line_columns || (sel.line_items[0] ? Object.keys(sel.line_items[0]) : []);
                const conf = CONF[sel.confidence] || CONF.medium;
                const st = STATUS[sel.review_status] || STATUS.pending;
                return (
                  <div style={{ ...card, padding: '18px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ fontSize: '15px', fontWeight: 700, minWidth: 0, wordBreak: 'break-word' }}>{sel.doc_label} <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gw-secondary, #64635f)' }}>· {sel.source_filename}</span></div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '12px' }}>
                        <span style={{ color: conf.c }}>판독 신뢰도 {conf.t}</span>
                        <span style={{ color: st.c, fontWeight: 600 }}>{st.t}</span>
                      </div>
                    </div>

                    {/* 헤더 필드 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px', marginBottom: '16px' }}>
                      {(meta.fields || Object.keys(sel.fields)).map((k) => (
                        <label key={k} style={{ fontSize: '12px', color: 'var(--gw-text, #32312e)' }}>
                          <span style={{ display: 'block', color: 'var(--gw-secondary, #64635f)', marginBottom: '2px' }}>{k}</span>
                          <input value={sel.fields[k] ?? ''} onChange={(e) => setField(k, e.target.value)} style={inp} />
                        </label>
                      ))}
                    </div>

                    {/* 품목 라인 */}
                    <div style={{ fontSize: '12px', fontWeight: 620, marginBottom: '6px' }}>품목 ({sel.line_items.length})</div>
                    <div style={{ overflowX: 'auto', border: '1px solid var(--gw-line, #eeeeec)', borderRadius: '8px', marginBottom: '14px' }}>
                      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '480px' }}>
                        <thead><tr>{cols.map((c) => <th key={c} style={th}>{c}</th>)}</tr></thead>
                        <tbody>
                          {sel.line_items.map((row, i) => (
                            <tr key={i}>
                              {cols.map((c) => (
                                <td key={c} style={{ padding: '2px', borderBottom: '1px solid var(--gw-line, #eeeeec)' }}>
                                  <input value={row[c] ?? ''} onChange={(e) => setCell(i, c, e.target.value)} style={{ ...inp, border: 'none', borderRadius: 0, fontSize: '12px', padding: '5px 6px' }} />
                                </td>
                              ))}
                            </tr>
                          ))}
                          {sel.line_items.length === 0 && <tr><td style={{ padding: '10px', fontSize: '12px', color: 'var(--gw-muted, #92918d)' }} colSpan={cols.length || 1}>추출된 품목이 없습니다.</td></tr>}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <Button variant="primary" onClick={() => save(null)} disabled={!dirty}>수정 저장</Button>
                      <Button variant="secondary" onClick={() => save('reviewed')}>검수 완료</Button>
                      <Button variant="secondary" onClick={() => save('rejected')}>반려</Button>
                      <Button variant="secondary" onClick={() => downloadExcel(sel)}>엑셀 내보내기</Button>
                      <Button variant="danger" onClick={() => remove(sel)}>삭제</Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </Page>
      </div>
    </AppShell>
  );
}
